// HayahAIInsights — Readiness + AI Summary + computed daily insight cards.
// Lifted out of HayahToday so the Today screen stays a pure dashboard and
// all AI-generated content lives where the user expects it: the AI tab.
//
// Rendered by HayahAICoach when its view === "insights" sub-tab.

import React, { useMemo } from "react";
import { getGeminiKey } from "./geminiAI";
import { useHayahPrefs } from "./hayahPrefs";
import { C, FS, FM, SecHead, useHayahTodayData, localDateStr } from "./HayahToday";
import ReadinessCard from "./hayah/ReadinessCard";

export default function HayahAIInsights() {
  const { prefs } = useHayahPrefs();
  const { data } = useHayahTodayData(prefs);
  const { intake, intakeGoal, burned, remaining, protein, carbs, fat, steps, stepsGoal, weightKg, sleep, fasting } = data;

  // ── Computed Insights (no AI) ──────────────────────────────────
  const todayInsights = useMemo(() => {
    const insights = [];
    const hour = new Date().getHours();
    const todayStr = localDateStr();

    let todayWorkout = null;
    try {
      const allWorkouts = JSON.parse(localStorage.getItem("sirat-workouts") || "[]");
      todayWorkout = allWorkouts.find(w => w.startedAt && localDateStr(new Date(w.startedAt)) === todayStr) || null;
    } catch {}

    // PROTEIN
    const proteinVal = protein?.value || 0;
    const proteinGoal = protein?.max || 150;
    const proteinPct = proteinGoal > 0 ? proteinVal / proteinGoal : 0;
    const proteinRem = Math.max(0, proteinGoal - proteinVal);
    let pColor, pStatus, pMsg;
    if (proteinPct >= 0.95) {
      pColor = C.green; pStatus = 'GOAL HIT';
      pMsg = 'Protein target reached. High protein intake protects muscle during fat loss and drives muscle protein synthesis after training.';
    } else if (proteinPct >= 0.65) {
      pColor = C.amber; pStatus = `${proteinRem}g REMAINING`;
      const scoops = Math.ceil(proteinRem / 25);
      pMsg = `${scoops === 1 ? 'One whey shake (25g)' : `${scoops} × whey scoops (~${scoops * 25}g)`} would close the gap. Aim for ${proteinGoal}g daily — essential for recomposition.`;
    } else {
      pColor = C.coral; pStatus = `${proteinRem}g SHORT`;
      pMsg = `Protein is the #1 lever for fat loss + muscle gain. Add a high-protein meal and a whey shake — ${proteinRem}g still needed today.`;
    }
    insights.push({ label: 'PROTEIN', color: pColor, status: pStatus, value: `${proteinVal}g / ${proteinGoal}g`, msg: pMsg });

    // CALORIES
    let calColor, calStatus, calValue, calMsg;
    if (burned > 0) {
      const deficit = burned - intake;
      calValue = `${intake} kcal in · ${burned} burned`;
      if (deficit >= 150 && deficit <= 600) {
        calColor = C.green; calStatus = `${deficit} KCAL DEFICIT`;
        calMsg = 'Ideal recomposition deficit. You\'re burning more than you\'re eating while keeping protein high — this is the fat-loss-with-muscle sweet spot.';
      } else if (deficit > 600) {
        calColor = C.amber; calStatus = `${deficit} KCAL DEFICIT`;
        calMsg = `Large deficit risks muscle loss. For recomposition, target 200–500 kcal below TDEE. Consider a protein-dense meal to close some of the gap.`;
      } else if (deficit < 0 && Math.abs(deficit) <= 300) {
        calColor = C.amber; calStatus = `${Math.abs(deficit)} KCAL SURPLUS`;
        calMsg = `Slight surplus on a training day is fine — supports muscle repair. Stay protein-high (${proteinGoal}g) and keep carbs from whole-food sources.`;
      } else if (deficit < -300) {
        calColor = C.coral; calStatus = `${Math.abs(deficit)} KCAL SURPLUS`;
        calMsg = 'Significant surplus. Trim refined carbs and fats for the rest of the day to stay on track with fat loss. Training hard and eating in surplus leads to fat gain, not muscle gain, without a plan.';
      } else {
        calColor = C.green; calStatus = 'BALANCED';
        calMsg = 'Calorie intake closely matches burn — on track for recomposition. Protein intake is the key driver now.';
      }
    } else {
      calValue = `${intake} / ${intakeGoal} kcal`;
      const remCal = intakeGoal - intake;
      if (remCal >= 0) {
        calColor = remCal < 250 ? C.amber : C.green;
        calStatus = `${remCal} KCAL LEFT`;
        calMsg = remCal < 250 ? `Almost at your daily goal — be mindful of snacking. Save remaining calories for a protein-rich meal.` : `${remCal} kcal remaining. Spread remaining intake across protein-rich meals to stay satiated and hit protein targets.`;
      } else {
        calColor = C.coral; calStatus = `${Math.abs(remCal)} KCAL OVER`;
        calMsg = 'Over daily target. Compensate with an extra walk or keeping tomorrow\'s intake clean. Don\'t skip meals — stay consistent.';
      }
    }
    insights.push({ label: 'CALORIES', color: calColor, status: calStatus, value: calValue, msg: calMsg });

    // TRAINING
    if (todayWorkout) {
      const mins = todayWorkout.duration ? Math.round(todayWorkout.duration / 60) : (todayWorkout.durationSec ? Math.round(todayWorkout.durationSec / 60) : 0);
      const totalSets = todayWorkout.sets || (todayWorkout.exercises || []).reduce((s, ex) => s + (ex.sets || []).filter(set => set.done !== false).length, 0);
      const topEx = (todayWorkout.exercises || []).slice(0, 2).map(ex => ex.name).join(' · ');
      insights.push({
        label: 'TRAINING', color: C.teal, status: 'SESSION LOGGED',
        value: `${todayWorkout.name || 'Workout'}${mins > 0 ? ` · ${mins} min` : ''}${totalSets > 0 ? ` · ${totalSets} sets` : ''}`,
        msg: `${topEx ? `${topEx} · ` : ''}PT-guided progressive overload is the most effective stimulus for recomposition. Eat protein within 2h post-workout to maximise muscle protein synthesis.`,
      });
    }

    // CREATINE
    let creColor, creStatus, creMsg;
    if (todayWorkout) {
      creColor = C.teal; creStatus = 'TAKE POST-WORKOUT';
      creMsg = 'Post-workout is the optimal creatine window. 3–5g with your protein shake — creatine + protein together enhances muscle cell uptake after resistance training.';
    } else {
      creColor = C.amber; creStatus = 'DAILY DOSE';
      creMsg = `Take 3–5g creatine monohydrate ${hour < 14 ? 'with breakfast or lunch' : 'with your next meal'}. Consistency matters more than timing — saturation is the goal. Creatine is the most evidence-backed supplement for strength and lean mass gains.`;
    }
    insights.push({ label: 'CREATINE', color: creColor, status: creStatus, value: '3–5g monohydrate', msg: creMsg });

    // RECOVERY
    if (sleep?.totalMins > 60) {
      const sleepH = (sleep.totalMins / 60);
      const deepH = sleep.deep > 0 ? (sleep.deep / 60) : 0;
      let rColor, rStatus, rMsg;
      if (sleepH >= 7.5) {
        rColor = C.green; rStatus = 'WELL RECOVERED';
        rMsg = `${sleepH.toFixed(1)}h sleep supports peak GH release and muscle repair. ${deepH > 0 ? `${deepH.toFixed(1)}h deep sleep detected — ` : ''}Your body is primed for a quality training session today.`;
      } else if (sleepH >= 6) {
        rColor = C.amber; rStatus = 'PARTIAL RECOVERY';
        rMsg = `${sleepH.toFixed(1)}h is borderline. Sleep deprivation elevates cortisol and blunts muscle protein synthesis. Aim for 7.5h+ on training nights — consider an earlier bedtime.`;
      } else {
        rColor = C.coral; rStatus = 'UNDER-RECOVERED';
        rMsg = `${sleepH.toFixed(1)}h is insufficient for a recomposition goal. Low sleep suppresses testosterone, spikes cortisol, and impairs fat loss. Prioritise sleep tonight above all else.`;
      }
      insights.push({ label: 'RECOVERY', color: rColor, status: rStatus, value: `${sleepH.toFixed(1)}h sleep${deepH > 0 ? ` · ${deepH.toFixed(1)}h deep` : ''}`, msg: rMsg });
    }

    return insights;
  }, [intake, intakeGoal, burned, protein, sleep]);

  const hasGeminiKey = getGeminiKey();

  return (
    <div style={{
      flex: 1, overflowY: 'auto', height: '100%',
      fontFamily: FS, color: C.ink,
      background: 'radial-gradient(ellipse at 50% -5%, rgba(95,209,185,0.06), transparent 50%), #070b09',
      WebkitOverflowScrolling: 'touch',
    }}>
      {/* No Gemini key — gentle nudge instead of broken sections */}
      {!hasGeminiKey && (
        <div style={{ padding: '24px 16px' }}>
          <div style={{
            background: 'rgba(95,209,185,0.06)', border: '1px solid rgba(95,209,185,0.2)',
            borderRadius: 14, padding: '16px 18px',
          }}>
            <div style={{ fontFamily: FM, fontSize: 10, color: C.teal, letterSpacing: '1.6px', textTransform: 'uppercase', marginBottom: 8 }}>
              AI Insights
            </div>
            <div style={{ fontFamily: FS, fontSize: 13.5, color: C.ink, lineHeight: 1.6 }}>
              Add a Gemini API key in <strong>Hayah Settings → AI Assistant</strong> to unlock readiness scoring and personalised daily insights.
            </div>
          </div>
        </div>
      )}

      {/* 01 · READINESS — extracted to <ReadinessCard /> so the
          Exercise → Today panel can mount the same component and the
          two surfaces never drift. */}
      {hasGeminiKey && (
        <div style={{ padding: '14px 14px 0' }}>
          <SecHead n="01" title="READINESS" color={C.teal} />
          <ReadinessCard showHeader={false} />
        </div>
      )}

      {/* 02 · TODAY (computed insight cards) */}
      <div style={{ padding: '14px 14px 32px' }}>
        <SecHead n="02" title="TODAY" color={C.amber} right={
          <span style={{ fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: '0.8px' }}>
            {new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </span>
        } />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {todayInsights.map((ins, i) => (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.025)', borderRadius: 12, padding: '11px 14px',
              border: `1px solid ${ins.color}1f`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontFamily: FM, fontSize: 9, color: ins.color, fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', opacity: 0.9 }}>
                  {ins.label}
                </span>
                <span style={{ fontFamily: FM, fontSize: 8.5, color: ins.color, letterSpacing: '0.8px', textTransform: 'uppercase', opacity: 0.65 }}>
                  {ins.status}
                </span>
              </div>
              <div style={{ fontFamily: FM, fontSize: 12.5, color: C.ink, fontWeight: 600, marginBottom: 4, lineHeight: 1.3 }}>
                {ins.value}
              </div>
              <div style={{ fontFamily: FS, fontSize: 12, color: C.faint, lineHeight: 1.6 }}>
                {ins.msg}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`@keyframes hayah-blink-dot{0%,100%{opacity:1}50%{opacity:0.2}}`}</style>
    </div>
  );
}
